import api from './api';
import VendorService from './vendorService';
import { 
    GetStorageSectionsResponse,
    SaveStorageRequest,
    SaveStorageResponse,
    CreateStorageTabRequest,
    CreateStorageTabResponse
} from '@/types/Storage.types';

const { aggregatedServiceAPI, pimcoreServiceAPI, vendorServiceAPI } = api;

const StorageService = {
    /**
     * Fetch storage onload data including dropdowns
     * Uses existing VendorService.getVendorOnload() which returns storage dropdown options
     * @returns Promise with storage onload API response
     */
    getStorageOnload: async (): Promise<any> => {
        try {
            const response = await VendorService.getVendorOnload();
            return response;
        } catch (error) {
            console.error("Error fetching storage onload data:", error);
            throw error;
        }
    },

    /**
     * Fetch storage sections for a specific assessment
     * GET /activeProjects/sections/storage?assessmentId={assessmentId}
     * @param assessmentId - The assessment ID
     * @returns Promise with storage sections response containing storageTab array
     */
    getStorageSections: async (
        assessmentId: string | number
    ): Promise<GetStorageSectionsResponse> => {
        try {
            const url = `activeProjects/sections/storage?assessmentId=${assessmentId}`;
            
            const response = await vendorServiceAPI.get(url);
            
            return response.data;
        } catch (error) {
            console.error("❌ [StorageService] Error fetching storage sections:", error);
            throw error;
        }
    },

    /**
     * Save or update storage section data
     * PUT /vendor/productspecification/{sectionId}/storage
     * Used for workflow save/next actions and share for review
     * @param sectionId - The storage tab section ID (not product spec ID)
     * @param storageData - Complete storage form data with optional transitionObject
     * @returns Promise with saved storage response
     */
    saveStorage: async (
        sectionId: string | number,
        storageData: SaveStorageRequest
    ): Promise<SaveStorageResponse> => {
        try {
            console.log('💾 [StorageService] Saving storage form data:', {
                sectionId,
                sectionName: storageData.sectionName,
                hasTransitionObject: !!storageData.transitionObject
            });
            
            const url = `vendor/productspecification/${sectionId}/storage`;
            
            const response = await vendorServiceAPI.put(
                url,
                storageData
            );
            
            console.log('✅ [StorageService] Storage form saved successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error("❌ [StorageService] Error saving storage data:", error);
            throw error;
        }
    },

    /**
     * Create a new storage tab
     * POST /shared/StorageTab/createStorageTab
     * @param payload - Storage tab creation data with section name and vendor assessment ID
     * @returns Promise with created storage tab details
     */
    createStorageTab: async (
        payload: CreateStorageTabRequest
    ): Promise<CreateStorageTabResponse> => {
        try {
            const url = 'shared/StorageTab/createStorageTab';
            
            console.log('➕ [StorageService] Creating storage tab:', payload);
            const response = await pimcoreServiceAPI.post(url, payload);
            
            console.log('✅ [StorageService] Storage tab created successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error("❌ [StorageService] Error creating storage tab:", error);
            throw error;
        }
    },

    /**
     * Delete a storage section by its ID
     * POST /shared/StorageTab/deleteStorageTab
     * @param storageTabId - The storage tab ID to delete
     * @returns Promise with deletion response
     */
    deleteStorage: async (storageTabId: string | number): Promise<any> => {
        try {
            console.log('🗑️ [StorageService] Deleting storage section:', storageTabId);
            
            const url = 'shared/StorageTab/deleteStorageTab';
            const payload = { id: Number(storageTabId) };
            
            const response = await pimcoreServiceAPI.post(url, payload);
            
            console.log('✅ [StorageService] Storage section deleted successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error("❌ [StorageService] Error deleting storage section:", error);
            throw error;
        }
    }
};

export default StorageService;

